import { datadogRum } from '@datadog/browser-rum';
import type {
  RumEvent,
  RumFetchResourceEventDomainContext,
} from '@datadog/browser-rum';
import { FullStory, isInitialized } from '@fullstory/browser';
import { isTeardownAbortedQuery } from 'context/consumer-web-bridge/teardown-signal';
import { readEmbedContext } from 'utils/embed-context';
import { Config } from '../constants';

type ApolloErrorContext = Error & {
  clientErrors: unknown[];
  extraInfo: unknown;
  graphQLErrors: unknown[];
  message: string;
  networkError: TypeError & { cause?: Record<string, string> };
};

const errorIsApolloNetworkErrorWithCause = (
  object: unknown
): object is ApolloErrorContext => {
  return (
    !!object &&
    typeof object === 'object' &&
    'networkError' in object &&
    typeof (object as ApolloErrorContext).networkError === 'object' &&
    (object as ApolloErrorContext).networkError !== null &&
    'cause' in (object as ApolloErrorContext).networkError
  );
};

// The RUM SDK turns every `securitypolicyviolation` event into an unhandled
// error, and it does so unconditionally — there is no init option to opt out
// (see trackReportError in @datadog/browser-rum-core). Partners who embed the
// flow under their own CSP generate these in bulk, which burns through the
// per-session error event rate limit and pushes out the real errors.
// `beforeSend` returning false is checked before the rate limiter, so dropping
// them here keeps the budget for errors we can act on.
//
// Only report-only violations are dropped. `disposition: 'report'` means the
// browser reported the violation and then loaded the resource anyway, so there
// is nothing behind it to fix; `disposition: 'enforce'` means something was
// actually blocked — that is how we would find out a partner's policy is
// killing the third-party subframes TokenEx needs, so those still go to RUM.
//
// `error.csp` is the exact marker for a CSP violation: only
// buildRawReportErrorFromCspViolation populates it, and errorCollection copies
// it straight through to the event. Matching on it rather than on the
// `csp_violation:` message prefix keeps this working if the SDK ever rewords
// that string — and the message is not always present on the delivered event.
const isReportOnlyCspViolation = (event: RumEvent): boolean =>
  event.type === 'error' && event.error?.csp?.disposition === 'report';

export const init = (env: string) => {
  datadogRum.init({
    applicationId: Config.APPLICATION_ID,
    clientToken: Config.CLIENT_TOKEN,
    defaultPrivacyLevel: 'mask-user-input',
    env,
    sessionSampleRate: 100,
    service: 'consumer-app',
    site: 'datadoghq.com',
    trackUserInteractions: true,
    trackResources: true,
    usePartitionedCrossSiteSessionCookie: true,
    enableExperimentalFeatures: ['feature_flags'],
    // eslint-disable-next-line no-underscore-dangle
    version: global.__VERSION_STRING__ || 'test',
    allowedTracingUrls: [
      'https://api.updater.com',
      'https://api.staging.updater.com',
    ],
    beforeSend: (event, context) => {
      if (isReportOnlyCspViolation(event)) {
        return false;
      }

      if (
        event.type === 'error' &&
        'error' in context &&
        isTeardownAbortedQuery(context.error)
      ) {
        return false;
      }

      if (event.type === 'resource') {
        if (
          event.resource.method === 'DELETE' &&
          event.resource.url.endsWith('/v2/sessions')
        ) {
          console.log('discarding logout API call');
          return false;
        }
        let parsedBody;
        try {
          parsedBody = JSON.parse(
            (context as RumFetchResourceEventDomainContext).requestInit
              ?.body as string
          );
        } catch (e) {
          parsedBody = {};
        }
        if (
          event.resource.url.endsWith('/graphql') &&
          event.resource.method === 'POST'
        ) {
          // eslint-disable-next-line no-param-reassign
          event.context = {
            ...event.context,
            graphQLOperationName: parsedBody.operationName,
            graphQLOperationType: parsedBody.query?.match(/^([\w]+)\s/)[1],
          };
        }
      }

      if (
        event.type === 'error' &&
        'error' in context &&
        errorIsApolloNetworkErrorWithCause(context.error)
      ) {
        event.context = {
          ...event.context,
          ...(context.error.networkError?.cause || {}),
        };
      }

      if (isInitialized()) {
        const fsSessionUrl = FullStory('getSession', { format: 'url' });
        event.context!.fullStoryUrl = fsSessionUrl; // eslint-disable-line
      }

      return true;
    },
  });

  // How the app is embedded, on every event. Partners either iframe the flow
  // into their own site or point a mobile webview at it directly, and the two
  // fail differently — a webview's host app can refuse the third-party
  // subframes TokenEx needs. Without this, telling those apart after the fact
  // means inferring it from user agents and request payload sizes.
  const embed = readEmbedContext();
  datadogRum.setGlobalContextProperty('isIframed', embed.isIframed);
  datadogRum.setGlobalContextProperty(
    'embedAncestorOrigins',
    embed.embedAncestorOrigins
  );
  datadogRum.setGlobalContextProperty(
    'embedAncestorCount',
    embed.embedAncestorCount
  );
};
